import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Heart,
  XCircle,
  MapPin,
  Building,
  Calendar,
  Volume2,
  BookOpen,
} from "lucide-react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import ses1 from "../audio/rota1.mp3";

// API URL'nizi .env dosyasından güvenle alın
const API_URL = process.env.REACT_APP_API_URL;

// Token'dan kullanıcı ID'sini güvenli bir şekilde çözen yardımcı fonksiyon
const getUserIdFromToken = (token) => {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.user_id || null;
  } catch (e) {
    console.error("Token çözümlenirken hata oluştu:", e);
    return null;
  }
};

// --- Bileşenler (Daha Temiz Bir Yapı İçin Ayrıldı) ---

// Bildirim Toast'ı
const NotificationToast = ({ message, onClose }) => {
  if (!message) return null;
  return (
    <div className="fixed top-5 right-5 z-50 bg-green-500 text-white py-2 px-4 rounded-lg shadow-lg flex items-center animate-fade-in-down">
      <p>{message}</p>
      <button
        onClick={onClose}
        className="ml-2 p-1 rounded-full hover:bg-green-600"
      >
        <XCircle size={20} />
      </button>
    </div>
  );
};

// Yükleniyor Ekranı (Skeleton Loader)
const LoadingSkeleton = () => (
  <div className="bg-gray-900 min-h-screen p-4 md:p-8">
    <div className="max-w-6xl mx-auto animate-pulse">
      {/* Geri Butonu Skeleton */}
      <div className="h-6 w-32 bg-gray-700 rounded-md mb-8"></div>
      {/* Başlık Resmi Skeleton */}
      <div className="h-96 bg-gray-800 rounded-2xl mb-8"></div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Ana İçerik Skeleton */}
        <div className="lg:col-span-2 space-y-6">
          <div className="h-24 bg-gray-800 rounded-xl"></div>
          <div className="p-6 bg-gray-800 rounded-xl space-y-4">
            <div className="h-8 w-1/3 bg-gray-700 rounded-md"></div>
            <div className="h-4 bg-gray-700 rounded-md"></div>
            <div className="h-4 bg-gray-700 rounded-md"></div>
            <div className="h-4 w-3/4 bg-gray-700 rounded-md"></div>
          </div>
        </div>
        {/* Yan Menü Skeleton */}
        <div className="space-y-6">
          <div className="h-64 bg-gray-800 rounded-xl"></div>
          <div className="h-32 bg-gray-800 rounded-xl"></div>
        </div>
      </div>
    </div>
  </div>
);

// Hata Ekranı
const ErrorDisplay = ({ onRetry }) => (
  <div className="min-h-screen bg-gray-900 text-gray-50 flex flex-col items-center justify-center p-6 text-center">
    <XCircle className="w-20 h-20 text-red-500 mb-4" />
    <h2 className="text-3xl font-bold text-white">Bir Sorun Oluştu</h2>
    <p className="text-gray-400 mt-2 max-w-md">
      Mekan detayları yüklenirken beklenmedik bir hata oluştu. Lütfen internet
      bağlantınızı kontrol edip tekrar deneyin.
    </p>
    <button
      onClick={onRetry}
      className="mt-8 px-6 py-3 bg-blue-600 text-white font-semibold rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300 transform hover:-translate-y-1"
    >
      Tekrar Dene
    </button>
  </div>
);

export default function MekanDetay() {
  const { mekanId } = useParams();
  const navigate = useNavigate();

  const [mekan, setMekan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [notification, setNotification] = useState(null);

  const showNotification = (message) => {
    setNotification(message);
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchMekanData = async () => {
    setLoading(true);
    setError(false);
    try {
      const mekanResponse = await axios.get(
        `${API_URL}/api/mekanlar/${mekanId}`
      );
      setMekan(mekanResponse.data);

      const token = localStorage.getItem("token");
      if (token) {
        const currentUserId = getUserIdFromToken(token);
        setUserId(currentUserId);
        if (currentUserId) {
          try {
            const favoritesResponse = await axios.get(
              `${API_URL}/api/favorites/${currentUserId}/${mekanId}`
            );
            setIsFavorited(favoritesResponse.data.isFavorited);
          } catch (favErr) {
            console.error("Favori durumu kontrol hatası:", favErr);
          }
        }
      }
    } catch (err) {
      console.error("API Hatası:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMekanData();
  }, [mekanId]);

  const handleFavoriteToggle = async () => {
    if (favoriteLoading || !userId) return;
    setFavoriteLoading(true);
    try {
      if (isFavorited) {
        await axios.delete(`${API_URL}/api/favorites/${userId}/${mekanId}`);
        setIsFavorited(false);
        showNotification("Favorilerden kaldırıldı!");
      } else {
        await axios.post(`${API_URL}/api/favorites`, { userId, mekanId });
        setIsFavorited(true);
        showNotification("Favorilere eklendi!");
      }
    } catch (err) {
      console.error("Favori işlemi hatası:", err);
      showNotification("İşlem sırasında bir hata oluştu.");
    } finally {
      setFavoriteLoading(false);
    }
  };

  if (loading) return <LoadingSkeleton />;
  if (error || !mekan) return <ErrorDisplay onRetry={fetchMekanData} />;

  // --- SEO ve Harita Bilgileri ---
  const title = `${mekan.ad} - ${mekan.il} | Mekânname`;
  const description = `${mekan.ad} hakkında bilgiler, konum ve ziyaret ipuçları.`;
  const canonical = `https://mekanname.com/mekan/${mekanId}`;

  // Düzeltilmiş ve daha güvenilir harita URL'leri
  const mapEmbedUrl = `https://maps.google.com/maps?q=${mekan.latitude},${mekan.longitude}&hl=tr&z=15&output=embed`;
  const mapLinkUrl = `https://www.google.com/maps/search/?api=1&query=${mekan.latitude},${mekan.longitude}`;

  const jsonLd = {
    /* ... Mevcut JSON-LD veriniz ... */
  };

  return (
    <HelmetProvider>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        {/* Diğer meta etiketleriniz... */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="bg-gray-900 min-h-screen font-sans text-gray-200">
        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          {/* Üst Gezinme */}
          <button
            onClick={() => navigate(-1)}
            className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-gray-400 hover:text-blue-400 transition-colors duration-200"
          >
            <ArrowLeft size={20} />
            Geri Dön
          </button>

          {/* Başlık Alanı */}
          <div className="relative w-full h-72 md:h-96 rounded-2xl overflow-hidden shadow-2xl mb-12 ring-1 ring-white/10">
            <img
              src={`${API_URL}/api/mekan-image/${encodeURIComponent(mekan.ad)}`}
              alt={mekan.ad}
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                e.target.src =
                  "https://placehold.co/1200x500/1a202c/9ca3af?text=Görsel+Bulunamadı";
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/95 via-gray-900/50 to-transparent"></div>
            <div className="absolute bottom-0 left-0 p-6 md:p-10 w-full flex items-end justify-between">
              <div>
                <p className="text-sm font-medium text-blue-400 uppercase tracking-widest">
                  {mekan.ilce} / {mekan.il}
                </p>
                <h1
                  className="text-4xl md:text-6xl font-extrabold mt-1 text-white"
                  style={{ textShadow: "0 2px 10px rgba(0,0,0,0.7)" }}
                >
                  {mekan.ad}
                </h1>
              </div>
              {userId ? (
                <button
                  onClick={handleFavoriteToggle}
                  disabled={favoriteLoading}
                  className={`p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 ${
                    isFavorited
                      ? "bg-red-500 hover:bg-red-600"
                      : "bg-white/10 hover:bg-white/20"
                  } ${favoriteLoading && "opacity-50 cursor-not-allowed"}`}
                  title={
                    isFavorited ? "Favorilerden Kaldır" : "Favorilere Ekle"
                  }
                >
                  <Heart
                    className={`w-6 h-6 transition-colors duration-300 ${
                      isFavorited ? "text-white fill-current" : "text-gray-300"
                    }`}
                  />
                </button>
              ) : (
                <div className="text-sm text-gray-400 bg-gray-800/80 backdrop-blur-sm p-3 rounded-lg">
                  Favorilemek için{" "}
                  <Link
                    to="/login"
                    className="font-bold text-blue-400 hover:underline"
                  >
                    giriş yapın
                  </Link>
                  .
                </div>
              )}
            </div>
          </div>

          {/* İçerik Alanı (Grid) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Sol Sütun: Açıklama ve Ses */}
            <div className="lg:col-span-2 space-y-8">
              {/* Mekan Hakkında Kartı */}
              <div className="bg-gray-800 p-6 rounded-xl shadow-lg border border-white/10">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                  <Volume2 size={24} className="text-blue-400" />
                  Sesli Anlatım
                </h2>
                <audio
                  controls
                  className="w-full mt-2 h-10 rounded-lg bg-gray-700"
                >
                  <source src={ses1} type="audio/mpeg" />
                  Tarayıcınız ses öğesini desteklemiyor.
                </audio>
                <br /> <hr className="border-blue-400"/> <br />
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                  <BookOpen size={24} className="text-blue-400" />
                  Mekan Hakkında
                </h2>
                  {mekan.aciklama_bolumleri && mekan.aciklama_bolumleri.map((bolum, index) => (
                    <div key={index}>
                      <h3 className="text-lg font-semibold text-blue-300">{bolum.baslik}</h3>
                      <p>{bolum.icerik}</p>
                    </div>
                  ))}
              </div>
            </div>

            {/* Sağ Sütun: Harita ve Ek Bilgiler (Sticky) */}
            <div className="lg:sticky lg:top-8 self-start space-y-8">
              {/* Konum Kartı */}
              <div className="bg-gray-800 p-6 rounded-xl shadow-lg border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">Konum</h3>
                <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden border border-white/10">
                  <iframe
                    title={`Harita - ${mekan.ad}`}
                    src={mapEmbedUrl}
                    className="w-full h-full"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
                <a
                  href={mapLinkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full mt-4 text-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-semibold shadow-md"
                >
                  Google Haritalar'da Aç
                </a>
              </div>

              {/* Ek Bilgiler Kartı */}
              <div className="bg-gray-800 p-6 rounded-xl shadow-lg border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">Detaylar</h3>
                <ul className="space-y-4 text-gray-300">
                  <li className="flex justify-between items-center gap-4">
                    <span className="font-semibold text-gray-400 flex items-center gap-2">
                      <Building size={18} /> İl
                    </span>
                    <span className="text-right">{mekan.il}</span>
                  </li>
                  <li className="flex justify-between items-center gap-4">
                    <span className="font-semibold text-gray-400 flex items-center gap-2">
                      <MapPin size={18} /> İlçe
                    </span>
                    <span className="text-right">{mekan.ilce}</span>
                  </li>
                  {mekan.yıl && (
                    <li className="flex justify-between items-center gap-4">
                      <span className="font-semibold text-gray-400 flex items-center gap-2">
                        <Calendar size={18} /> Yapım Yılı
                      </span>
                      <span className="text-right">{mekan.yıl}</span>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </main>
      </div>
      <NotificationToast
        message={notification}
        onClose={() => setNotification(null)}
      />
    </HelmetProvider>
  );
}
